import { UseFormReturn } from 'react-hook-form';

export type ParsedServerErrors = {
  fieldErrors: Record<string, string>;
  nonFieldErrors: string[];
};

/**
 * Parse common server error shapes into { fieldErrors, nonFieldErrors }.
 * - expects payload like: { success: false, errors: { field: ["msg"] } }
 * - defensive: also handles direct { field: ["msg"] } or { message: "..." }.
 */
export function parseServerErrors(err: any): ParsedServerErrors {
  const fieldErrors: Record<string, string> = {};
  const nonFieldErrors: string[] = [];

  const payload = (err && (err.data ?? err.response ?? err)) ?? {};

  const toMessages = (v: any): string[] => {
    if (v === null || v === undefined) return [];
    if (Array.isArray(v)) return v.map(String).filter(Boolean);
    if (typeof v === 'string') return [v];
    if (typeof v === 'object')
      return Object.values(v).flatMap(toMessages).map(String).filter(Boolean);
    return [String(v)];
  };

  if (
    payload &&
    typeof payload === 'object' &&
    payload.errors &&
    typeof payload.errors === 'object'
  ) {
    for (const [k, v] of Object.entries(payload.errors)) {
      const msgs = toMessages(v);
      if (msgs.length) fieldErrors[k] = msgs.join('. ');
    }
  } else if (payload && typeof payload === 'object') {
    // fallback: parse direct keys
    for (const [k, v] of Object.entries(payload)) {
      if (['success', 'status'].includes(k)) continue;
      const msgs = toMessages(v);
      if (!msgs.length) continue;
      // treat alpha-ish keys as field errors (company_slug, email, etc.)
      if (/^[a-z0-9_]+$/i.test(k)) fieldErrors[k] = msgs.join('. ');
      else nonFieldErrors.push(...msgs);
    }
    if (payload.message && typeof payload.message === 'string')
      nonFieldErrors.push(payload.message);
    if (payload.detail && typeof payload.detail === 'string')
      nonFieldErrors.push(payload.detail);
  }

  // final fallback: err.message
  if (
    Object.keys(fieldErrors).length === 0 &&
    nonFieldErrors.length === 0 &&
    err &&
    typeof err.message === 'string'
  ) {
    nonFieldErrors.push(err.message);
  }

  return { fieldErrors, nonFieldErrors };
}

export function applyServerErrors(
  methods: UseFormReturn<any>,
  err: any
): ParsedServerErrors {
  const parsed = parseServerErrors(err);

  // map fieldErrors -> methods.setError
  for (const [field, message] of Object.entries(parsed.fieldErrors)) {
    methods.setError(field as any, { type: 'server', message });
  }

  return parsed;
}
